import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input } from "../ui";
import { SettingsSection } from "./SettingsSection";

/**
 * Settings → Calendar feed (I1): a private address any calendar app can subscribe to. It shows planner
 * blocks and due tasks next to appointments; read-only, edits happen in Drang. What goes in is chosen with
 * the checkboxes, which only change the address's query (the same subscription keeps working without them).
 */
export function CalendarFeedSettings() {
  const { t } = useTranslation();
  const [base, setBase] = useState<string | null>(null);
  const [opts, setOpts] = useState({ plan: true, due: true, todo: false });
  const [copied, setCopied] = useState(false);

  const call = (method: "GET" | "POST" | "DELETE") =>
    fetch("/api/internal/feed", { method, credentials: "same-origin" }).then((r) =>
      r.ok ? (r.json() as Promise<{ url: string | null }>) : null,
    );
  const change = (method: "POST" | "DELETE") => void call(method).then((d) => d && setBase(d.url));
  useEffect(() => {
    void call("GET").then((d) => d && setBase(d.url));
  }, []);

  const query = [!opts.plan && "plan=0", !opts.due && "due=0", opts.todo && "todo=1"].filter(Boolean);
  const url = base ? (query.length ? `${base}?${query.join("&")}` : base) : null;

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const step = "text-[13px] leading-relaxed";
  return (
    <SettingsSection
      id="settings-feed"
      intro={t("feed.hint")}
      summary={t("settings.summary.feed")}
      title={t("feed.title")}
      testId="settings-feed"
      folded
    >
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-[11.5px] text-neutral-500">{t("feed.include")}</legend>
        <label className="flex items-center gap-3 text-sm">
          <Checkbox
            checked={opts.plan}
            label={t("feed.plan")}
            onChange={(v) => setOpts({ ...opts, plan: v })}
          />
          <span>{t("feed.plan")}</span>
        </label>
        <label className="flex items-center gap-3 text-sm">
          <Checkbox checked={opts.due} label={t("feed.due")} onChange={(v) => setOpts({ ...opts, due: v })} />
          <span>{t("feed.due")}</span>
        </label>
        <label className="flex items-center gap-3 text-sm">
          <Checkbox
            checked={opts.todo}
            label={t("feed.todo")}
            onChange={(v) => setOpts({ ...opts, todo: v })}
          />
          <span>{t("feed.todo")}</span>
        </label>
      </fieldset>
      {url ? (
        <div className="flex flex-col gap-2" data-testid="feed-address">
          <div className="flex items-center gap-2">
            <Input
              compact
              readOnly
              value={url}
              aria-label={t("feed.address")}
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 font-mono"
              data-testid="feed-url"
            />
            <Button size="sm" variant="secondary" onClick={() => void copy()}>
              {copied ? t("feed.copied") : t("feed.copy")}
            </Button>
          </div>
          <p className="text-[12px] text-neutral-500">{t("feed.private")}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => change("POST")} data-testid="feed-renew">
              {t("feed.renew")}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => change("DELETE")} data-testid="feed-stop">
              {t("feed.stop")}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button onClick={() => change("POST")} data-testid="feed-make">
            {t("feed.make")}
          </Button>
        </div>
      )}
      <h3 className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
        {t("feed.howTitle")}
      </h3>
      <ul className={`list-disc pl-5 ${step}`}>
        <li>{t("feed.howThunderbird")}</li>
        <li>{t("feed.howGoogle")}</li>
        <li>{t("feed.howApple")}</li>
      </ul>
      <p className="text-[12px] text-neutral-500">{t("feed.refreshHint")}</p>
    </SettingsSection>
  );
}
